import React, { useState, useEffect, useCallback } from 'react';
import styles from './FolderNavigator.module.css';

// NOTA: La eliminación de archivos está deshabilitada por seguridad
// Los usuarios pueden crear, editar y renombrar archivos, pero no eliminarlos
const FolderNavigator = ({
  fileMap,
  selectedPath,
  onFileSelect,
  onFileDelete,
  onFileCreate,
  onFileRename,
  onFolderSelect,
  isLoading = false,
  children,
  currentProject = 'react-basic',
  availableProjects = [],
  onProjectChange
}) => {
  const [expandedFolders, setExpandedFolders] = useState(new Set());
  const [draggedItem, setDraggedItem] = useState(null);
  const [dragOverItem, setDragOverItem] = useState(null);
  const [editingFile, setEditingFile] = useState(null);
  const [editValue, setEditValue] = useState('');

  // Convertir fileMap a estructura de carpetas - VERSIÓN MEJORADA
  const buildFolderStructure = useCallback(() => {
    console.log('🔍 buildFolderStructure called');
    console.log('📁 fileMap size:', fileMap.size);
    console.log('📁 fileMap keys:', Array.from(fileMap.keys()));
    
    const structure = {};

    // Crear un Set para rastrear todas las carpetas que contienen archivos
    const foldersWithFiles = new Set();
    
    // Set para rastrear carpetas vacías marcadas
    const emptyFolders = new Set();

    // Primera pasada: identificar carpetas vacías marcadas
    for (const path of fileMap.keys()) {
      if (path.startsWith('__EMPTY_FOLDER__')) {
        const folderPath = path.replace('__EMPTY_FOLDER__', '');
        emptyFolders.add(folderPath);
      }
    }

    // Segunda pasada: construir la estructura y rastrear carpetas con archivos
    for (const path of fileMap.keys()) {
      // Saltar las entradas de carpetas vacías marcadas
      if (path.startsWith('__EMPTY_FOLDER__')) {
        continue;
      }
      
      console.log('📄 Processing path:', path);
      const parts = path.split('/');
      let current = structure;

      for (let i = 0; i < parts.length; i++) {
        const part = parts[i];
        if (i === parts.length - 1) {
          // Es un archivo
          current[part] = { type: 'file', path, content: fileMap.get(path) };
          console.log(`📄 Added file: ${part} at path: ${path}`);
          
          // Marcar todas las carpetas padre como que contienen archivos
          let parentPath = '';
          for (let j = 0; j < parts.length - 1; j++) {
            parentPath = parentPath ? `${parentPath}/${parts[j]}` : parts[j];
            foldersWithFiles.add(parentPath);
          }
        } else {
          // Es una carpeta
          if (!current[part]) {
            current[part] = { type: 'folder', children: {} };
            console.log(`📁 Created folder: ${part}`);
          }
          current = current[part].children;
        }
      }
    }

    // Tercera pasada: agregar carpetas vacías marcadas
    for (const emptyFolderPath of emptyFolders) {
      const parts = emptyFolderPath.split('/');
      let current = structure;

      for (let i = 0; i < parts.length; i++) {
        const part = parts[i];
        if (i === parts.length - 1) {
          // Es la carpeta vacía final
          if (!current[part]) {
            current[part] = { type: 'folder', children: {}, isEmpty: true };
          }
        } else {
          // Es una carpeta padre
          if (!current[part]) {
            current[part] = { type: 'folder', children: {} };
          }
          current = current[part].children;
        }
      }
    }

    console.log('🏗️ Final structure:', structure);
    console.log('📁 Expanded folders:', Array.from(expandedFolders));
    
    return structure;
  }, [fileMap, expandedFolders]);

  // Auto-expandir carpetas cuando se agregan archivos
  useEffect(() => {
    console.log('🔄 Auto-expand effect - fileMap size:', fileMap.size);
    
    if (fileMap.size === 0) {
      console.log('📁 No files in fileMap, skipping auto-expand');
      return;
    }
    
    const newExpanded = new Set();
    let hasChanges = false;

    for (const path of fileMap.keys()) {
      // Saltar las entradas de carpetas vacías marcadas
      if (path.startsWith('__EMPTY_FOLDER__')) {
        continue;
      }
      
      const parts = path.split('/');
      let currentPath = '';

      for (let i = 0; i < parts.length - 1; i++) {
        currentPath = currentPath ? `${currentPath}/${parts[i]}` : parts[i];
        newExpanded.add(currentPath);
        console.log('📁 Adding folder to expanded:', currentPath);
      }
    }

    // Siempre actualizar si hay archivos
    if (newExpanded.size > 0) {
      console.log('✅ Setting expanded folders:', Array.from(newExpanded));
      setExpandedFolders(newExpanded);
    }
  }, [fileMap]);

  // Función para verificar si una carpeta está vacía
  const isFolderEmpty = useCallback((folderPath) => {
    for (const path of fileMap.keys()) {
      // Saltar las entradas de carpetas vacías marcadas
      if (path.startsWith('__EMPTY_FOLDER__')) {
        continue;
      }
      
      if (path.startsWith(folderPath + '/')) {
        return false; // La carpeta tiene archivos
      }
    }
    return true; // La carpeta está vacía
  }, [fileMap]);

  // Función para eliminar archivo sin eliminar carpetas vacías
  const handleFileDelete = useCallback((filePath) => {
    // No permitir eliminar entradas de carpetas vacías
    if (filePath.startsWith('__EMPTY_FOLDER__')) {
      console.log('⚠️ No se puede eliminar una entrada de carpeta vacía');
      return;
    }
    
    // DESHABILITADO: No permitir eliminación de archivos
    console.log('❌ Eliminación de archivos deshabilitada por seguridad');
    // alert('La eliminación de archivos está deshabilitada por seguridad.');
    // return;
    
    // Código original comentado:
    onFileDelete(filePath);

    // Verificar si la carpeta padre quedó vacía y mantenerla en el fileMap
    const parentPath = filePath.substring(0, filePath.lastIndexOf('/'));
    if (parentPath && isFolderEmpty(parentPath)) {
      // La carpeta quedó vacía, pero la mantenemos en el fileMap como carpeta vacía
      console.log(`Carpeta ${parentPath} quedó vacía pero se mantiene en el fileMap`);
      
      // Crear una entrada especial para la carpeta vacía en el fileMap
      // Usamos un prefijo especial para identificar carpetas vacías
      const emptyFolderKey = `__EMPTY_FOLDER__${parentPath}`;
      
      // Llamar a una función especial para mantener la carpeta vacía
      if (onFileCreate) {
        onFileCreate(emptyFolderKey, ''); // Crear entrada vacía para la carpeta
      }

      // Asegurar que la carpeta vacía permanezca expandida
      setExpandedFolders(prev => {
        const newExpanded = new Set(prev);
        newExpanded.add(parentPath);
        return newExpanded;
      });
    }
  }, [onFileDelete, isFolderEmpty, onFileCreate]);

  const toggleFolder = (folderPath) => {
    const newExpanded = new Set(expandedFolders);
    if (newExpanded.has(folderPath)) {
      newExpanded.delete(folderPath);
    } else {
      newExpanded.add(folderPath);
    }
    setExpandedFolders(newExpanded);
    
    // NO llamar a onFolderSelect cuando se hace clic en una carpeta
    // Solo manejar la expansión/contracción de la carpeta
  };

  // Función mejorada para manejar la selección de archivos
  const handleFileClick = useCallback((filePath) => {
    console.log('📁 FolderNavigator: File clicked:', filePath);
    
    // No permitir seleccionar entradas de carpetas vacías
    if (filePath.startsWith('__EMPTY_FOLDER__')) {
      console.log('⚠️ No se puede seleccionar una entrada de carpeta vacía');
      return;
    }
    
    // Llamar a onFileSelect para seleccionar el archivo
    if (onFileSelect) {
      onFileSelect(filePath);
    }
    
    // NO llamar a onFolderSelect cuando se selecciona un archivo
    // Esto evita que se borre el mapa de carpetas
  }, [onFileSelect]);

  const getFileIcon = (path) => {
    // Si es una entrada de carpeta vacía, mostrar icono de carpeta vacía
    if (path && path.startsWith('__EMPTY_FOLDER__')) {
      return '📁';
    }
    
    const ext = path.split('.').pop().toLowerCase();
    switch (ext) {
      case 'tsx':
      case 'jsx':
        return '⚛️';
      case 'ts':
      case 'js':
        return '📄';
      case 'css':
      case 'scss':
        return '🎨';
      case 'json':
        return '⚙️';
      case 'md':
        return '📝';
      case 'html':
        return '🌐';
      default:
        return '📁';
    }
  };

  const handleDragStart = (e, item) => {
    // No permitir arrastrar entradas de carpetas vacías
    if (item.path && item.path.startsWith('__EMPTY_FOLDER__')) {
      e.preventDefault();
      return;
    }
    
    setDraggedItem(item);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, item) => {
    // No permitir soltar en entradas de carpetas vacías
    if (item.path && item.path.startsWith('__EMPTY_FOLDER__')) {
      return;
    }
    
    e.preventDefault();
    setDragOverItem(item);
  };

  const handleDrop = (e, targetItem) => {
    e.preventDefault();
    
    // No permitir soltar en entradas de carpetas vacías
    if (targetItem.path && targetItem.path.startsWith('__EMPTY_FOLDER__')) {
      setDraggedItem(null);
      setDragOverItem(null);
      return;
    }
    
    if (draggedItem && targetItem && draggedItem.path !== targetItem.path) {
      // Aquí implementarías la lógica de mover archivos
      console.log(`Mover ${draggedItem.path} a ${targetItem.path}`);
    }
    setDraggedItem(null);
    setDragOverItem(null);
  };

  const handleFileRename = (filePath) => {
    // No permitir renombrar entradas de carpetas vacías
    if (filePath.startsWith('__EMPTY_FOLDER__')) {
      console.log('⚠️ No se puede renombrar una entrada de carpeta vacía');
      return;
    }
    
    setEditingFile(filePath);
    setEditValue(filePath.split('/').pop());
  };

  const saveRename = () => {
    if (editingFile && editValue) {
      // No permitir renombrar entradas de carpetas vacías
      if (editingFile.startsWith('__EMPTY_FOLDER__')) {
        console.log('⚠️ No se puede renombrar una entrada de carpeta vacía');
        setEditingFile(null);
        setEditValue('');
        return;
      }
      
      const newPath = editingFile.replace(/\/[^/]+$/, `/${editValue}`);
      onFileRename(editingFile, newPath);
    }
    setEditingFile(null);
    setEditValue('');
  };

  const cancelRename = () => {
    setEditingFile(null);
    setEditValue('');
  };

  const renderTreeItem = (name, item, currentPath = '') => {
    const fullPath = currentPath ? `${currentPath}/${name}` : name;
    const isSelected = selectedPath === fullPath;
    const isExpanded = expandedFolders.has(fullPath);
    const isDragging = draggedItem && draggedItem.path === fullPath;
    const isDragOver = dragOverItem && dragOverItem.path === fullPath;

    if (item.type === 'folder') {
      return (
        <div key={fullPath} className={styles.treeItem}>
          <div
            className={`${styles.folderItem} ${isDragOver ? styles.dragOver : ''}`}
            onDragOver={(e) => handleDragOver(e, { type: 'folder', path: fullPath })}
            onDrop={(e) => handleDrop(e, { type: 'folder', path: fullPath })}
          >
            <div className={styles.folderHeader}>
              <button
                className={styles.folderToggle}
                onClick={() => toggleFolder(fullPath)}
                disabled={isLoading}
              >
                {isExpanded ? '📂' : '📁'}
              </button>
              <span className={styles.itemName}>{name}</span>
              <div className={styles.itemActions}>
                <button
                  className={styles.actionButton}
                  onClick={(e) => {
                    e.stopPropagation();
                    const newFileName = prompt('Nombre del nuevo archivo:');
                    if (newFileName) {
                      const newFilePath = `${fullPath}/${newFileName}`;
                      onFileCreate(newFilePath, '');
                    }
                  }}
                  title="Crear archivo"
                >
                  +
                </button>
              </div>
            </div>
          </div>
          {isExpanded && (
            <div className={styles.folderContent}>
              {Object.entries(item.children).map(([childName, childItem]) =>
                renderTreeItem(childName, childItem, fullPath)
              )}
              {/* Mostrar mensaje si la carpeta está vacía pero expandida */}
              {Object.keys(item.children).length === 0 && (
                <div className={styles.emptyFolder}>
                  <span className={styles.emptyFolderText}>📁 Carpeta vacía</span>
                  <button
                    className={styles.createFileButton}
                    onClick={(e) => {
                      e.stopPropagation();
                      const newFileName = prompt('Nombre del nuevo archivo:');
                      if (newFileName) {
                        const newFilePath = `${fullPath}/${newFileName}`;
                        onFileCreate(newFilePath, '');
                      }
                    }}
                    title="Crear archivo en esta carpeta"
                  >
                    + Crear archivo
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      );
    } else {
      // No renderizar entradas de carpetas vacías como archivos normales
      if (fullPath.startsWith('__EMPTY_FOLDER__')) {
        return null;
      }
      
      return (
        <div
          key={fullPath}
          className={`${styles.treeItem} ${isDragging ? styles.dragging : ''}`}
          draggable
          onDragStart={(e) => handleDragStart(e, { type: 'file', path: fullPath })}
          onDragOver={(e) => handleDragOver(e, { type: 'file', path: fullPath })}
          onDrop={(e) => handleDrop(e, { type: 'file', path: fullPath })}
        >
          <div
            className={`${styles.fileItem} ${isSelected ? styles.selected : ''} ${isDragOver ? styles.dragOver : ''}`}
            onClick={() => handleFileClick(fullPath)}
            onDoubleClick={() => handleFileRename(fullPath)}
          >
            <span className={styles.fileIcon}>{getFileIcon(fullPath)}</span>
            {editingFile === fullPath ? (
              <input
                type="text"
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') saveRename();
                  if (e.key === 'Escape') cancelRename();
                }}
                onBlur={saveRename}
                className={styles.renameInput}
                autoFocus
              />
            ) : (
              <span className={styles.itemName}>{name}</span>
            )}
            <div className={styles.itemActions}>
              <button
                className={styles.actionButton}
                onClick={(e) => {
                  e.stopPropagation();
                  handleFileRename(fullPath);
                }}
                title="Renombrar (doble clic)"
              >
                ✏️
              </button>
              {/* Botón de eliminar deshabilitado por seguridad */}
              <button
                className={styles.actionButton}
                onClick={(e) => {
                  e.stopPropagation();
                  handleFileDelete(fullPath);
                }}
                title="Eliminar"
                disabled={isLoading}
              >
                🗑️
              </button>
            </div>
          </div>
        </div>
      );
    }
  };

  const folderStructure = buildFolderStructure();
  
  console.log('🎯 Rendering FolderNavigator');
  console.log('📁 folderStructure:', folderStructure);
  console.log('📁 expandedFolders:', Array.from(expandedFolders));

  return (
    <div className={styles.navigator}>
      <div className={styles.header}>
        {/* Selector de Proyectos */}
        <div className={styles.projectSelector}>
          <div className={styles.projectSelectorContent}>


            <select
              value={currentProject}
              onChange={(e) => onProjectChange(e.target.value)}
              className={styles.projectSelect}
              disabled={isLoading}
            >
              {availableProjects.map(project => (
                <option key={project.id} value={project.id}>
                  {project.icon} {project.name}
                </option>
              ))}
            </select>

            {/* <h3 className={styles.projectTitle}>📂 Proyectos</h3> */}
            {isLoading ? (
              <div className={styles.loading}>🔄 Sincronizando...</div>
            ) : (
              <>
                {children}
              </>
            )}
          </div>

        </div>

      </div>
      <div className={styles.tree}>
        {console.log('🌳 Rendering tree items:', Object.keys(folderStructure))}
        {Object.entries(folderStructure).map(([name, item]) => {
          console.log('🌳 Rendering item:', name, item.type);
          return renderTreeItem(name, item);
        })}
      </div>
      <p className={styles.projectDescription}>
        {availableProjects.find(p => p.id === currentProject)?.description || ''}
      </p>
    </div>
  );
};

export default FolderNavigator; 